import React, { ReactElement, memo } from 'react';

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import Select, {
  ActionMeta,
  CSSObjectWithLabel,
  DropdownIndicatorProps,
  FormatOptionLabelMeta,
  GroupBase,
  MenuListProps,
  MultiValue,
  SingleValue,
  StylesConfig,
  components,
} from 'react-select';

import { Button } from '@components/Base/Button';

import messages from './messages';
type Option =
  | {
      label: string;
      value: string | number;
    }
  | number
  | string;

export type DropdownValue = MultiValue<Option> | SingleValue<Option>;

export interface DropdownProps {
  id: string;
  value: DropdownValue;
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  name?: string;
  options: Array<Option>;
  onChange: (newValue: MultiValue<Option> | SingleValue<Option>, actionMeta: ActionMeta<Option>) => void;
  placeholder?: string | ReactElement;
  isSearchable: boolean;
  isMulti?: boolean;
  customStyles?: object;
  controlShouldRenderValue?: boolean;
  selectProps?: object;
  formatOptionLabel?: (
    option: string | number | { label: string; value: string | number },
    formatOptionLabelMeta: FormatOptionLabelMeta<string | number | { label: string; value: string | number }>,
  ) => React.ReactNode;
  isSearchIcon?: boolean;
  isShowAddFlowBtn?: boolean;
}

export const reactSelectDefaultStyles: StylesConfig<Option, boolean, GroupBase<Option>> = {
  input: (props: CSSObjectWithLabel) => ({
    ...props,
    input: {
      boxShadow: 'none !important',
      border: 'none !important',
    },
  }),
  placeholder: (props: CSSObjectWithLabel) => ({
    ...props,
    color: 'gray-400',
  }),
  control: (props: CSSObjectWithLabel) => ({
    ...props,
    boxShadow: 'none',
    fontSize: '0.875rem',
  }),
  option: (props: CSSObjectWithLabel, state: { isSelected: boolean; isFocused: boolean }) => ({
    ...props,
    fontSize: '0.875rem',
    backgroundColor: state.isSelected || state.isFocused ? 'gray-100' : 'white',
    color: 'gray-900',
  }),
  multiValue: (props: CSSObjectWithLabel) => ({
    ...props,
    backgroundColor: '#F3F4F6',
    borderRadius: '12px',
    margin: '2px',
    color: '#56595C',
  }),
  multiValueLabel: () => ({
    color: '#56595C',
    paddingLeft: '10px',
  }),
  multiValueRemove: (props: CSSObjectWithLabel) => ({
    ...props,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ':hover': {
      backgroundColor: 'transparent',
      color: '#56595C',
    },
  }),
};

const DropdownIndicator: React.FC<DropdownIndicatorProps<Option, boolean, GroupBase<Option>>> = (props) => {
  const color = 'text-gray-900';
  return (
    <components.DropdownIndicator {...props}>
      <ChevronDownIcon className={`h-6 w-6 ${color}`} />
    </components.DropdownIndicator>
  );
};

const DropdownSearchIndicator: React.FC<DropdownIndicatorProps<Option, boolean, GroupBase<Option>>> = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <MagnifyingGlassIcon className="w-5 h-5 text-gray-900" />
    </components.DropdownIndicator>
  );
};

const NoOptionsMessage = () => (
  <div className="text-sm font-normal leading-[16px] mx-0 text-gray-900 p-2">No results found</div>
);

const CustomMenuList = (props: MenuListProps<Option, boolean, GroupBase<Option>>) => {
  return (
    <components.MenuList {...props}>
      {props.children}
      <div className="sticky bottom-0 bg-white border-t border-gray-300 h-9">
        <Button className="flex justify-between items-center w-full rounded-md bg-white text-black enabled:hover:bg-primary-ghost-hover outline-primary-ghost enabled:active:bg-primary-ghost-active">
          <span className="inline-flex items-center text-gray-900 gap-2 p-0">
            <PlusIcon className="w-4 h-4 text-sm text-gray-900" />
            <FormattedMessage {...messages.create_new} />
          </span>
        </Button>
      </div>
    </components.MenuList>
  );
};

export function Dropdown({
  id,
  value,
  isDisabled,
  isLoading,
  isClearable = false,
  name = 'dropdown',
  options,
  onChange,
  placeholder,
  isSearchable = false,
  isMulti = false,
  customStyles = {},
  controlShouldRenderValue = true,
  formatOptionLabel,
  isSearchIcon = false,
  isShowAddFlowBtn = false,
}: DropdownProps) {
  return (
    <Select
      inputId={id}
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isClearable={isClearable}
      isSearchable={isSearchable}
      name={name}
      placeholder={placeholder}
      options={options}
      styles={{
        ...reactSelectDefaultStyles,
        ...customStyles,
      }}
      components={{
        ...Select,
        DropdownIndicator: isSearchIcon ? DropdownSearchIndicator : DropdownIndicator,
        NoOptionsMessage: NoOptionsMessage,
        ...(isShowAddFlowBtn ? { MenuList: CustomMenuList } : {}),
      }}
      classNames={{
        control: () => classNames(`border rounded !shadow-0 !bg-white !border-gray-200 w-full`),
        placeholder: () => 'text-sm font-normal leading-[16px] mx-0 text-gray-400',
        input: () => 'm-0 p-0',
        indicatorSeparator: () => 'hidden',
        indicatorsContainer: () => 'h-8',
        singleValue: () => 'text-sm font-normal leading-[16px] mx-0 text-gray-900',
        valueContainer: () => 'p-0',
        menuList: () => 'flex flex-col',
        option: ({ isSelected }) =>
          classNames('px-1.5 py-2 text-sm', isSelected ? 'bg-gray-100' : 'hover:bg-gray-100 bg-white'),
      }}
      closeMenuOnSelect={!isMulti}
      isMulti={isMulti}
      controlShouldRenderValue={controlShouldRenderValue}
      menuPlacement="auto"
      backspaceRemovesValue={false}
      formatOptionLabel={formatOptionLabel}
    />
  );
}

export default memo(Dropdown);
